import { AcademicTermYearModel } from 'src/app/services/models/study-period/study-period.model';

/** Converts a Gregorian year to Buddhist Era; passes through years outside the plausible Gregorian range. */
export function toBuddhistYear(year: number): number {
  return year > 0 && year < 2400 ? year + 543 : year;
}

/** Distinct Buddhist years present in `terms`, as strings sorted descending numerically. */
export function buildAcademicYearOptions(terms: AcademicTermYearModel[]): string[] {
  const years = new Set(terms.map((term) => String(toBuddhistYear(term.year))));
  return Array.from(years).sort((a, b) => Number(b) - Number(a));
}

/** Distinct, trimmed term names for a given Buddhist year, sorted ascending (Thai locale). */
export function buildAcademicTermNameOptions(
  terms: AcademicTermYearModel[],
  buddhistYear: string | null,
): string[] {
  if (buddhistYear == null || buddhistYear === '') {
    return [];
  }

  const names = new Set(
    terms
      .filter((term) => String(toBuddhistYear(term.year)) === buddhistYear)
      .map((term) => String(term.name).trim())
      .filter((name) => name.length > 0),
  );

  return Array.from(names).sort((a, b) => a.localeCompare(b, 'th'));
}

/** Finds the term matching `termName` and `buddhistYear`; picks the highest id when duplicates match. */
export function findAcademicTerm(
  terms: AcademicTermYearModel[],
  termName: string | null,
  buddhistYear: string | null,
): AcademicTermYearModel | undefined {
  if (termName == null || termName === '' || buddhistYear == null || buddhistYear === '') {
    return undefined;
  }

  const trimmedName = termName.trim();

  return terms
    .filter(
      (term) =>
        String(term.name).trim() === trimmedName &&
        String(toBuddhistYear(term.year)) === buddhistYear,
    )
    .reduce<AcademicTermYearModel | undefined>(
      (best, term) => (!best || term.id > best.id ? term : best),
      undefined,
    );
}

/** The chronologically newest term (highest year, then term name, then id); undefined for an empty list. */
export function defaultAcademicTerm(
  terms: AcademicTermYearModel[],
): AcademicTermYearModel | undefined {
  return terms.reduce<AcademicTermYearModel | undefined>(
    (best, term) => (!best || compareTerms(term, best) > 0 ? term : best),
    undefined,
  );
}

function compareTerms(a: AcademicTermYearModel, b: AcademicTermYearModel): number {
  if (a.year !== b.year) {
    return a.year - b.year;
  }

  const nameDiff = Number(a.name) - Number(b.name);
  if (!Number.isNaN(nameDiff) && nameDiff !== 0) {
    return nameDiff;
  }

  return a.id - b.id;
}
